import React, { useRef, useEffect, useState } from 'react';
import styles from "./AnimatedScrollMultilineText.module.css";

export type HorizontalAlignment = 'left' | 'center' | 'right';

type AnimatedScrollMultilineTextProps = {
  lines: string[];
  colorFrom: string;
  colorTo: string;
  lineHeightFrom: number;
  lineHeightTo: number;
  fontSize?: string | number;
  fontWeight?: string | number;
  slideDistance?: number; // px
  horizontalAlign?: HorizontalAlignment;
};

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function lerpColor(a: string, b: string, t: number) {
  // Accepts hex colors only, returns hex
  const ah = a.replace('#', '');
  const bh = b.replace('#', '');
  const ar = parseInt(ah.substring(0, 2), 16);
  const ag = parseInt(ah.substring(2, 4), 16);
  const ab = parseInt(ah.substring(4, 6), 16);
  const br = parseInt(bh.substring(0, 2), 16);
  const bg = parseInt(bh.substring(2, 4), 16);
  const bb = parseInt(bh.substring(4, 6), 16);
  const rr = Math.round(lerp(ar, br, t));
  const rg = Math.round(lerp(ag, bg, t));
  const rb = Math.round(lerp(ab, bb, t));
  return `#${rr.toString(16).padStart(2, '0')}${rg.toString(16).padStart(2, '0')}${rb.toString(16).padStart(2, '0')}`;
}

const AnimatedScrollMultilineText: React.FC<AnimatedScrollMultilineTextProps> = React.memo(({
  lines,
  colorFrom,
  colorTo,
  lineHeightFrom,
  lineHeightTo,
  fontSize = '2rem',
  fontWeight = 400, 
  slideDistance = 40,
  horizontalAlign = 'left',
}) => {

  const ref = useRef<HTMLDivElement>(null);
  // Target progress based on scroll, actual progress glides toward it
  const [progress, setProgress] = useState(0); // 0 = up, 1 = down
  const targetProgressRef = useRef(0);

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const winH = window.innerHeight;
      // If out of viewport, snap progress to 0 or 1
      if (rect.bottom < 0) {
        targetProgressRef.current = 1;
        return;
      }
      if (rect.top > winH) {
        targetProgressRef.current = 0;
        return;
      }
      // Calculate how far through the viewport the element is
      const visible = Math.max(0, Math.min(1, 1 - rect.top / winH));
      targetProgressRef.current = visible;
    };
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);
    handleScroll();
    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, []);

  // Glide animation: smoothly approach targetProgressRef.current
  // Easing function for more natural glide
  function easeInOut(t: number) {
    return t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
  }

  useEffect(() => {
    let frame: number;
    const animate = () => {
      setProgress(prev => {
        const target = targetProgressRef.current;
        // If out of view, snap instantly
        if (prev === target) return prev;
        if (target === 0 || target === 1) return target;
        // Responsive lerp with easeInOut
        const speed = 0.25; // much faster, but still smooth
        const eased = easeInOut(Math.abs(target - prev));
        const next = prev + (target - prev) * (speed + 0.25 * eased);
        if (Math.abs(next - target) < 0.002) return target;
        return next;
      });

      frame = requestAnimationFrame(animate);
    };
    frame = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(frame);
  }, []);

  // Interpolate color and line height
  const color = lerpColor(colorFrom, colorTo, progress);
  const lineHeight = lerp(lineHeightFrom, lineHeightTo, progress);
  const translateY = lerp(0, -slideDistance, progress);
  const resolvedTextAlign = horizontalAlign || 'left';

  return (
    <div
      ref={ref}
      className={styles.animatedScrollMultilineText}
      style={{
        fontSize,
        fontWeight,
        color,
        lineHeight,
        transform: `translateY(${translateY}px)`,
        textAlign: resolvedTextAlign,
      }}
    >
      {lines.map((line, i) => (
        <span
          key={i}
          className={styles.animatedScrollMultilineLine}
          style={{ textAlign: resolvedTextAlign }}
        >
          {line}
        </span>
      ))}
    </div>
  );
});

export default AnimatedScrollMultilineText;